import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { getUrl } from "aws-amplify/storage";
import { FileDown, Lightbulb } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import ReportWrapper, { ReportType } from "../../abstract/Report";
import DefaultLogo from "../../assets/images/report-default-logo.png";
import Distribution from "../../components/report/charts/Distribution";
import Reviews, {
  BlastulationReviewItem,
  FerilizationReviewItem,
  LiveBirthReviewItem,
  ReviewItem,
  ReviewStage,
} from "../../components/Reviews";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useReport } from "../../contexts/ReportContext";
import { useResource } from "../../contexts/ResourceContext";
import PDF from "../../pdf/PDF";
import { fontSizes, ReportColorStyle, ReportColorStyleName } from "../../theme";
import { reportPageWidth, EthnicityType } from "../../types";
import { getFileExtension } from "../../utils";
import {
  convertImageToBase64,
  enumKeyByValue,
  getAge,
  getBucketItemPath,
} from "../../utils";
import { ReportOverrideFooter } from "../EggFreezeCalculator/types";
import { Data as AnalysisTableData } from "./AnalysisTable";
import PatientInfoCard from "./PatientInfoCard";
import PatientInfoDrawer from "./PatientInfoDrawer";
import { AnalysisCards, OocyteAnalysis, RateStatusCards } from "./ReportInner";
import {
  FormData,
  FormResponse,
  GetCycleResponse,
  GetCycleResponseItem,
} from "./types";
import Progress from "../../components/Progress";
import SubmitButton from "../../components/SubmitButton";
import CardWithIcon from "../../components/cards/CardWithIcon";

const Report: React.FC<{ type: ReportType }> = ({ type }) => {
  const { cycleId } = useParams();
  const { apiService, setSubmitting } = useApi();
  const { showError, showSuccess } = useError();
  const { updateBreadcrumbs, updateActions } = useApp();
  const navigate = useNavigate();
  const [patientInfoExpanded, setPatientInfoExpanded] =
    useState<boolean>(false);
  const { response } = useResource<FormData, FormResponse>();
  const [logo, setLogo] = useState<string>(DefaultLogo);
  const logoRef = useRef<HTMLImageElement>(null);
  const distructionRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [cycle, setCycle] = useState<GetCycleResponseItem | null>(null);
  const { user } = useAuth();
  const { colorStyle } = useReport();
  const [contactDetails, setContactDetails] =
    useState<ReportOverrideFooter | null>(null);
  const formData = {} as FormData;
  const theme = useTheme();

  const { translate } = useApp();
  const translations = useMemo(() => translate("oocyte"), [translate]);

  const [reviews, setReviews] = useState<ReviewItem[]>([
    {
      id: "1",
      stage: ReviewStage.fertilization,
      oocytes: null,
      predicted: null,
    },
    {
      id: "2",
      stage: ReviewStage.blastulation,
      oocytes: null,
      predicted: null,
    },
    {
      id: "3",
      stage: ReviewStage.liveBirth,
      successfulPregnancy: null,
      liveBirths: null,
    },
  ]);
  const [imageUrls, setImageUrls] = useState<{ [id: string]: string }>({});
  console.log("cycleId", cycleId);

  const loadImages = (c: GetCycleResponseItem) => {
    for (const image of c.images) {
      getUrl({
        path: getBucketItemPath(user.clinic, "oocyte", c.id, image.id, getFileExtension(image.filename) ),
      }).then((response) => {
        const imageUrl = response.url.href;

        convertImageToBase64(imageUrl).then((base64) => {
          setImageUrls((prev) => ({
            ...prev,
            [image.id]: base64,
          }));
        });
      });
    }
  };

  useEffect(() => {
    if (type === "report") {
      updateActions([
        <SubmitButton
          sx={{ width: 170 }}
          key={"export"}
          color={"blackButton"}
          onClick={exportToPDF}
        >
          <Stack direction={"row"} spacing={1}>
            <FileDown />
            <Typography variant="medium" fontSize={fontSizes.sm}>
              {translations.report.actions.exportPDF}
            </Typography>
          </Stack>
        </SubmitButton>,
      ]);
    }
    if (type === "analysis") {
      updateActions([
        <Button
          key={"create"}
          color="blackButton"
          onClick={() => navigate(`/oocyte/${cycleId}/report`)}
        >
          {translations.report.actions.previewReport}
        </Button>,
      ]);
    }
    if (type === "history") {
      updateActions([
        <Button
          key={"analysis"}
          onClick={() => navigate(`/oocyte/${cycleId}/analysis`)}
        >
          {translations.report.actions.analysis}
        </Button>,
        <Button
          key={"create"}
          color="blackButton"
          onClick={() => navigate(`/oocyte/${cycleId}/report`)}
        >
          {translations.report.actions.previewReport}
        </Button>,
      ]);
    }

    return () => {
      updateActions([]);
    };
  }, [type, cycle]);

  useEffect(() => {
    if (user?.config) {
      if (user.config.logo) {
        setLogo(user.config.logo);
      }
    }
    if (user?.config?.contactDetails) {
      setContactDetails(user.config.contactDetails);
    }
  }, [user]);

  useEffect(() => {
    const reviewsCopy = [...reviews];
    console.info("cycle", cycle);
    if (cycle?.u_fertilized) {
      reviewsCopy[0] = {
        ...reviewsCopy[0],
        oocytes: cycle.u_fertilized,
        predicted: cycle.p_fertilized,
      } as ReviewItem;
    }
    if (cycle?.u_blastocyst) {
      reviewsCopy[1] = {
        ...reviewsCopy[1],
        oocytes: cycle.u_blastocyst,
        predicted: cycle.p_blastulation,
      } as ReviewItem;
    }
    if (cycle?.u_livebirth) {
      reviewsCopy[2] = {
        ...reviewsCopy[2],
        liveBirths: cycle.u_livebirth,
      } as ReviewItem;
    }
    setReviews(reviewsCopy);
  }, [cycle]);

  useEffect(() => {
    console.log("xx Fetching cycle data");
    setSubmitting(true);
    apiService
      .get<GetCycleResponse>(`/oocyte/${cycleId}`)
      .then((response) => {
        setCycle(response.cycle);
        loadImages(response.cycle);
      })
      .catch((error) => {
        console.log("error", error);
        showError(translations.report.errors.errorFetchingCycleData);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [type]);

  useEffect(() => {
    const breadCrumbs = [
      {
        label: translations.breadcrumbs.oocyteAssessment,
        onClick: () => navigate("/oocyte", { replace: true }),
      },
      {
        label: translations.breadcrumbs.history,
        onClick: () => navigate(`/oocyte/${cycleId}`, { replace: true }),
      },
    ];
    if (type === "analysis") {
      breadCrumbs.push({
        label: translations.breadcrumbs.analysis,
        onClick: () => navigate(`/oocyte/${cycleId}/alysis`, { replace: true }),
      });
    }
    if (type === "report") {
      breadCrumbs.push({
        label: translations.breadcrumbs.report,
        onClick: () => {},
      });
    }
    updateBreadcrumbs(breadCrumbs);
  }, [type, cycle, translations, cycleId]);

  const reviewsCount = useMemo(() => {
    if (!cycle) {
      return 0;
    }
    if (!reviews || reviews.length < 3) return 0;
    return (
      Number(!!(reviews[0] as FerilizationReviewItem).oocytes) +
      Number(!!(reviews[1] as BlastulationReviewItem).oocytes) +
      Number(!!(reviews[2] as LiveBirthReviewItem).liveBirths)
    );
  }, [cycle, reviews]);

  const handleReviewSaved = async (review: any) => {
    const fields: any = {};
    const newReviews: any = [...reviews];
    console.log("review", review);
    if (review.stage === ReviewStage.fertilization) {
      newReviews[0] = {
        ...newReviews[0],
        oocytes: review.oocytes,
      };
    } else if (review.stage === ReviewStage.blastulation) {
      newReviews[1] = {
        ...newReviews[1],
        oocytes: review.oocytes,
      };
    } else if (review.stage === ReviewStage.liveBirth) {
      newReviews[2] = {
        ...newReviews[2],
        successfulPregnancy: review.successfulPregnancy,
        liveBirths: review.liveBirths,
      };
    }
    if (newReviews[0].oocytes) {
      fields["u_fertilized"] = newReviews[0].oocytes;
    }
    if (newReviews[1].oocytes) {
      fields["u_blastocyst"] = newReviews[1].oocytes;
    }
    if (newReviews[1].liveBirths) {
      fields["u_livebirth"] = newReviews[1].liveBirths;
    }
    try {
      setSubmitting(true);
      const response = await apiService.post(`/oocyte/${cycleId}`, { fields });
      if ((response as any).cycle?.[0]?.updated) {
        showSuccess(translations.report.messages.reviewDataSaved);
        setReviews(newReviews);
      } else {
        showError(translations.report.errors.errorSavingReviewData);
      }
    } catch (error) {
      console.log("error", error);
      showError(translations.report.errors.errorSavingReviewData);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const cycleImages = useMemo<AnalysisTableData[]>(() => {
    if (!cycle) return [];
    return cycle.images
      .sort((a, b) => (a.id > b.id ? 1 : -1))
      .map((image, index) => ({
        image: imageUrls[image.id],
        index: index + 1,
        score: image.u_overall,
        fertilization: image.p_fertilization,
        cleavage: image.p_cleavage,
        blastulation: image.p_blastulation,
        invalid: image.invalid,
      }));
  }, [cycle, cycle?.images, imageUrls]);

  const rates = useMemo(() => {
    if (!cycle || !cycle.images?.length) {
      return {
        maturityRate: 0,
        matureCount: 0,
        fertilizationRate: 0,
        fertilizedCount: 0,
        blastulationRate: 0,
        blastocystCount: 0,
      };
    }

    const total = cycle.images.length;

    const fertilizationRate = Math.round(
      cycle.images.reduce((sum, img) => sum + (img.p_fertilization ?? 0), 0) /
        total
    );
    const fertilizedCount = Math.round((fertilizationRate / 100) * total);

    const blastulationRate = Math.round(
      cycle.images.reduce((sum, img) => sum + (img.p_blastulation ?? 0), 0) /
        total
    );
    const blastocystCount = Math.round((blastulationRate / 100) * total);

    // Your existing maturityRate logic
    const maturityRate = Math.round(
      Math.random() * 10 + Math.min(Math.max(fertilizationRate, 5), 95) - 5
    );
    const matureCount = Math.round((maturityRate / 100) * total);

    return {
      maturityRate,
      matureCount,
      fertilizationRate,
      fertilizedCount,
      blastulationRate,
      blastocystCount,
    };
  }, [cycle]);

  const exportToPDF = useCallback(async () => {
    if (!cycle) return;
     const {
      maturityRate,
      fertilizationRate,
      blastulationRate,
      matureCount,
      fertilizedCount,
      blastocystCount,
    } = rates;
    
    const pdf = new PDF(
      theme.palette.reportColorStyles[
        colorStyle as ReportColorStyleName
      ] as ReportColorStyle,
      translations.report.pageTitle.report,
      contactDetails ?? undefined,
      reportPageWidth
    );



    setSubmitting(true);

    pdf.setDate(cycle?.created ? new Date(cycle?.created) : new Date());
    const logoElm = document.getElementById("report-logo") as HTMLImageElement;
    const logoSrc = logoElm?.getAttribute("src") || DefaultLogo;

    try {
      convertImageToBase64(logoSrc)
        .then((b64) => {
          const originalWidth =
            logoElm?.naturalWidth ?? logoElm?.clientWidth ?? 64;
          const originalHeight =
            logoElm?.naturalHeight ?? logoElm?.clientHeight ?? 64;

          const logoWidth = Math.min(originalWidth, 64);
          const logoHeight = (logoWidth / originalWidth) * originalHeight;

          pdf.setLogo(b64, {
            width: logoWidth,
            height: logoHeight,
          });
        })
        .catch((error) => {
          console.error("Error converting logo to base64", error);
          showError(translations.report.errors.errorRetrievingLogo);
        });
    } catch (error) {
      showError(translations.report.errors.errorRetrievingLogo);
    }

    const ethnicities = (cycle?.ethnicity ?? []).map((e) =>
      translate("general", `ethnicities.${enumKeyByValue(EthnicityType, e)}`)
    );

 
    const rows = [
      {
        columns: [
          {
            key: translations.report.patientInfo.name,
            value: cycle?.patient ?? "",
          },
          {
            key: translations.report.patientInfo.ethnicity,
            value: ethnicities.join(", "),
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.patientInfo.age,
            value: `${getAge(new Date(cycle?.birth ?? 0))}`,
          },
          {
            key: translations.report.patientInfo.numberOfOocytes,
            value: `${cycle?.images.length}`,
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.patientInfo.weight,
            value: `${cycle?.weight ?? "-"} kg`,
          },
        ],
      },
    ];
    pdf.setPatientInformation({ rows: rows });
    pdf.setLogo(null);
    pdf.build();
    pdf.addMargins(1);
    pdf.addSubTitle(translations.report.pdf.subtitles.cycleAnalysis);
    const cardWidth =
      (pdf.getPageSize().width - 2 * PDF.pageMargins.x - 20) / 3;
    const cardHeight = 116;
    const spacing = 10;
    pdf.addCard(
      translations.report.pdf.cards.maturityStatus,
      maturityRate,
      `${matureCount} ${translations.report.pdf.phrases.outOf} ${cycle?.images.length} ${translations.report.pdf.phrases.mature}`,
      cardWidth,
      cardHeight,
      0,
      "#FFFFFF",
      "#E5E5E5",
      "#000000"
    );

    pdf.addCard(
      translations.report.pdf.cards.fertilizationRate,
      fertilizationRate,
      `${fertilizedCount} ${translations.report.pdf.phrases.outOf} ${cycle?.images.length} ${translations.report.pdf.phrases.fertilized}`,
      cardWidth,
      cardHeight,
      cardWidth + spacing,
      "#FFFFFF",
      "#E5E5E5",
      "#000000"
    );
    pdf.addCard(
      translations.report.pdf.cards.blastocystConversion,
      blastulationRate,
      `${blastocystCount} ${translations.report.pdf.phrases.outOf} ${cycle?.images.length} ${translations.report.pdf.phrases.blastocyst}`,
      cardWidth,
      cardHeight,
      2 * (cardWidth + spacing),
      "#FFFFFF",
      "#E5E5E5",
      "#000000"
    );
    pdf.setY(pdf.getY() + cardHeight + spacing);
    await pdf.addHtmlCanvas(document.getElementById("distribution-chart")!);
    pdf.addPage();
    await pdf.addHtmlCanvas(document.getElementById("analysis-cards")!);
    pdf.addMargins(2);
    pdf.addSubTitle(translations.report.pdf.subtitles.oocyteSpecificAnalysis);
    await pdf.addHtmlCanvas(document.getElementById("analysis-table")!);

    setSubmitting(false);

    pdf.save(`Oocyte_Quality_Assessment_${user.clinic}.pdf`);
  }, [logo, formData, response]);

  if (!cycle) {
    return <Progress />;
  }

  return (
    <Box>
      <ReportWrapper
        type={type}
        title={""}
        sections={[
          {
            title: translations.report.sections.patientInformation,
            component: (
              <PatientInfoCard
                type={type}
                onExpand={() => setPatientInfoExpanded(true)}
                cycle={cycle}
              />
            ),
          },
          {
            title: translations.report.sections.cycleAnalysis,
            component: (
              <Stack spacing={2}>
                {cycle && (
                  <RateStatusCards
                    maturityRate={rates.maturityRate}
                    fertilizationRate={rates.fertilizationRate}
                    blastulationRate={rates.blastulationRate}
                  />
                )}
                <Distribution
                  eggs={cycle.images.map((img) => img.p_blastulation)}
                />
                <AnalysisCards
                  liveBirthProbability={cycle.p_livebirth}
                  aneuploidyRiskEstimate={cycle.p_aneuploidy}
                />
              </Stack>
            ),
          },
          {
            title: translations.report.sections.oocyteSpecificAnalysis,
            component: <OocyteAnalysis type={type} data={cycleImages} />,
          },
          type === "analysis"
            ? {
                title: "",
                component: (
                  <Reviews
                    reviews={reviews}
                    onSave={handleReviewSaved}
                    count={reviewsCount}
                  />
                ),
              }
            : null,
          {
            title: "",
            component: (
              <CardWithIcon
                title={translations.report.learnMore.title}
                description={
                  <Link
                    target="_blank"
                    href={translations.report.learnMore.linkUrl}
                  >
                    {translations.report.learnMore.linkLabel}
                  </Link>
                }
                icon={<Lightbulb />}
              />
            ),
          },
        ]}
      />
      {cycle && (
        <PatientInfoDrawer
          open={patientInfoExpanded}
          cycle={cycle}
          onClose={() => setPatientInfoExpanded(false)}
        />
      )}
    </Box>
  );
};

export default Report;
